import { DispositionsDiagram_EntityFragment } from './graphql/DispositionsDiagram.generated';

/**
 * Narrow contract for the BDG resolver + trigger-info helpers: only the
 * fields actually read. Root entities and depth-1 recipient entities both
 * satisfy this shape via a single cast at the walker boundary (see
 * `collectDownstreamRecipients`). Anything wider would let callers reach
 * into `household`, `entityDiagrams`, etc., which are undefined on
 * downstream recipient shapes.
 */
export type BdgResolverEntityInput = Pick<
  DispositionsDiagram_EntityFragment,
  'id' | 'subtype'
>;

/**
 * Per-entity metadata used to wire BDG-aware behavior across the adapter,
 * the form sync hook, and the diagram toggle UI.
 */
export interface BdgTriggerInfo {
  /** Individuals whose death triggers distribution. */
  triggeringClientIds: string[];
  /** True when any policy on the account has `survivorship=true`. */
  isSurvivorship: boolean;
  /**
   * Client id in the OFF position of the death-order Switch. Null when
   * no toggle should be shown (non-BDG entity, survivorship, <2 primary
   * clients).
   */
  defaultTriggerClientId: string | null;
}

export function getBdgTriggerInfo(
  entity: BdgResolverEntityInput | null | undefined,
  possiblePrimaryClients: { id: string }[]
): BdgTriggerInfo | null {
  const subtype = entity?.subtype;
  if (!subtype) return null;

  let triggeringClientIds: string[] = [];
  let isSurvivorship = false;

  if (subtype.__typename === 'RetirementPersonalAccount') {
    const ownerId = subtype.owner?.individual?.id;
    triggeringClientIds = ownerId ? [ownerId] : [];
  } else if (subtype.__typename === 'InsurancePersonalAccount') {
    const policyHolderIds = new Set<string>();
    (subtype.policies ?? []).forEach((policy) => {
      (policy?.policyHolders ?? []).forEach((ph) => {
        if (ph?.id) policyHolderIds.add(ph.id);
      });
    });
    triggeringClientIds = Array.from(policyHolderIds);
    isSurvivorship = (subtype.policies ?? []).some(
      (policy) => policy?.survivorship ?? false
    );
  } else {
    return null;
  }

  let defaultTriggerClientId: string | null = null;
  if (
    !isSurvivorship &&
    triggeringClientIds.length === 1 &&
    possiblePrimaryClients.length === 2 &&
    possiblePrimaryClients.some((c) => c.id === triggeringClientIds[0])
  ) {
    defaultTriggerClientId = triggeringClientIds[0] ?? null;
  }

  return { triggeringClientIds, isSurvivorship, defaultTriggerClientId };
}
